import { memo, useContext } from 'react';
import { ResponsivePie } from '@nivo/pie';
import type { PieChart } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { ChartHoverText } from './Hover';
import { theme } from './theme';
import { formatValueWithUnit } from '@trinity/chat-components/utils/chart';
import { TemplateTooltip } from './TemplateTooltip';
import { replaceTemplateValues } from './template-utils';

type Props = {
  chart: PieChart;
  colors: string[];
  animate?: boolean;
  columnN?: number;
  unit?: string;
  isPrefix?: boolean;
  isInteractive?: boolean;
};

export const NivoPieChart = memo(
  ({ chart, colors, animate = false, columnN, unit, isPrefix, isInteractive = true }: Props) => {
    const [, setOpen] = useContext(LegendCollapseToggleContext);

    const returnedData = chart.data;
    const data = [];

    for (let i = 0; i < returnedData.length; i++) {
      data.push({ ...returnedData[i], color: colors[i] });
    }

    return (
      <div style={{ height: '100%', width: '100%', position: 'relative' }}>
        <ResponsivePie
          data={data}
          margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
          innerRadius={0.5}
          padAngle={0.7}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          colors={colors}
          isInteractive={isInteractive}
          borderWidth={1}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]],
          }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#333333"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLabelsSkipAngle={10}
          arcLinkLabelsStraightLength={24}
          arcLabelsTextColor="#FFFFFF"
          arcLabel={(d) => formatValueWithUnit({ value: d.value, unit, isPrefix })}
          tooltip={
            isInteractive
              ? (e) => {
                  const { datum } = e;
                  // If a tooltip template is provided, use it
                  if (chart.tooltipTemplate) {
                    const sliceExtra = chart.sliceData?.[String(datum.id)] ?? {};
                    const templateValues = {
                      label: datum.label,
                      value: datum.value,
                      id: datum.id,
                      xAxisTitle: chart.xAxisTitle,
                      yAxisTitle: chart.yAxisTitle,
                      unit: unit || '',
                      ...sliceExtra,
                    };
                    const content = replaceTemplateValues(chart.tooltipTemplate, templateValues);
                    return <TemplateTooltip content={content} />;
                  }
                  // Otherwise use the default tooltip
                  return (
                    <ChartHoverText
                      name={datum.label}
                      count={datum.value}
                      columnN={columnN}
                      unit={unit}
                      isPrefix={isPrefix}
                    />
                  );
                }
              : undefined
          }
          onClick={
            isInteractive
              ? (data) => {
                  setOpen(true);
                }
              : undefined
          }
          animate={animate}
          theme={theme}
        />
      </div>
    );
  }
);
